import api from '@/api';
import type { APIPatchConsentResponse, Consent } from '@/api/queries/me/types/me';
import useMeQuery from '@/api/queries/me/useMeQuery';
import { customQueryClient } from '@/config/query-client';
import type { Translate } from '@/plugins/i18n/types';
import { useMutation } from '@tanstack/vue-query';
import { AxiosError } from 'axios';
import type { Ref } from 'vue';
import { inject } from 'vue';
import { useToast } from 'vue-toastification';

const patchConsent = async (uuid?: string, consent?: Partial<Record<Consent['type'], boolean>>) => {
  if (!uuid) throw new AxiosError('Auth UUID is necessary to patch user consent');

  const res = await api<APIPatchConsentResponse>({
    method: 'patch',
    url: `/user/${uuid}/consent`,
    data: {
      ...consent,
    },
  });

  return res;
};

export function useConsentMutation(consents: Ref<Partial<Record<Consent['type'], boolean>>>) {
  const { auth } = useMeQuery();
  const toast = useToast();
  const t = inject('t') as Translate;

  const { mutateAsync: updateConsent, isPending: isLoading } = useMutation({
    mutationFn: () => patchConsent(auth.value?.uuid, consents.value),

    onSuccess: () => {
      customQueryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success(t('general.save_success'));
    },
    onError: () => toast.error(t('general.error.general')),
  });

  return { updateConsent, isLoading };
}
